import defaultOptions from '@atlassian/jira-common-constants/src/fetch-default-options';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { applyObservabilityHeaders } from '@atlassian/jira-fetch/src/utils/observability-headers';
import { getTraceId } from '@atlassian/jira-fetch/src/utils/trace-id.tsx';
import {
	PRIORITY_SCHEME_DESCRIPTION_FIELD,
	PRIORITY_SCHEME_NAME_FIELD,
} from '../common/ui/constants';
import type {
	PriorityOnServer,
	AsyncTask,
	PrioritySchemeValidationError,
} from '../common/ui/types';
import { refineAsyncTask } from '../common/ui/utils';
import { refinePriorityOnServer } from '../common/utils';
import type {
	PriorityOnServerPageResponse,
	FetchResponse,
	RefinedFetchError,
	SavePrioritySchemeSuccessResponse,
} from './types';

/**
 * Fetch from the given URL, returning the json response.
 * <p>
 * If there are any HTTP errors the promise is rejected with a {@link FetchError}.
 *
 * @param {string} url - to fetch the JSON from
 * @param {object} options - for the fetch call
 * @returns promise containing the JSON
 * @throws {@link FetchError} if there was an HTTP Error. This will reject the promise.
 */
export const fetchWithStatus = async (
	url: string,
	options: RequestInit = {},
): Promise<FetchResponse> => {
	const response = await fetch(
		url,
		applyObservabilityHeaders(url, { ...defaultOptions, ...options }),
	);

	if (!response.ok) {
		const traceId = getTraceId(response);
		if (traceId !== undefined && traceId.length > 0) {
			throw new FetchError(
				response.status,
				`Fetch call failed with status code: ${response.status}`,
				traceId,
				response,
			);
		} else {
			throw new FetchError(response.status, undefined, undefined, response);
		}
	}

	if (response.status === 204) {
		return new Promise<FetchResponse>((resolve) => {
			resolve({
				status: response.status,
				body: null,
			});
		});
	}

	const body: unknown = await response.json();
	return new Promise<FetchResponse>((resolve) => {
		resolve({
			status: response.status,
			body,
		});
	});
};

export const refineValidationError = (
	obj: unknown,
): PrioritySchemeValidationError[] | undefined => {
	if (!obj || typeof obj !== 'object') return undefined;

	if (!('errors' in obj) || typeof obj.errors !== 'object' || !obj.errors) return undefined;

	const validationErrors: PrioritySchemeValidationError[] = [];

	if ('name' in obj.errors && typeof obj.errors.name === 'string') {
		validationErrors.push({
			field: PRIORITY_SCHEME_NAME_FIELD,
			message: obj.errors.name,
		});
	}

	if ('description' in obj.errors && typeof obj.errors.description === 'string') {
		validationErrors.push({
			field: PRIORITY_SCHEME_DESCRIPTION_FIELD,
			message: obj.errors.description,
		});
	}

	return validationErrors;
};

export const refineFetchError = async (obj: unknown): Promise<RefinedFetchError | undefined> => {
	if (!obj || typeof obj !== 'object') return undefined;

	if (!('statusCode' in obj) || typeof obj.statusCode !== 'number') return undefined;

	if (
		!('originalResponse' in obj) ||
		typeof obj.originalResponse !== 'object' ||
		!obj.originalResponse ||
		!('json' in obj.originalResponse) ||
		typeof obj.originalResponse.json !== 'function'
	)
		return undefined;

	const responseJson: object = await obj.originalResponse.json();

	return {
		statusCode: obj.statusCode,
		responseJson,
	};
};

export const refineEditPrioritySchemeResponse = (
	schemeId: string,
	obj: unknown,
): SavePrioritySchemeSuccessResponse | undefined => {
	if (!obj || typeof obj !== 'object') return undefined;

	if (!('status' in obj) || typeof obj.status !== 'number') return undefined;

	if (!('body' in obj) || typeof obj.body !== 'object') return undefined;

	// TODO: Update this to deal with updated edit scheme response
	if (obj.body === null)
		return {
			status: obj.status,
			body: {
				id: schemeId,
			},
		};

	const asyncTask: AsyncTask | undefined = refineAsyncTask(obj.body);
	if (!asyncTask)
		return {
			status: obj.status,
			body: { id: schemeId },
		};

	return {
		status: obj.status,
		body: { id: schemeId, ...asyncTask },
	};
};

export const refineCreatePrioritySchemeResponse = (
	obj: unknown,
): SavePrioritySchemeSuccessResponse | undefined => {
	if (!obj || typeof obj !== 'object') return undefined;

	if (!('status' in obj) || typeof obj.status !== 'number') return undefined;

	if (!('body' in obj) || typeof obj.body !== 'object') return undefined;

	if (!obj.body) return undefined;

	if (!('id' in obj.body) || typeof obj.body.id !== 'string') return undefined;
	const schemeId: string = obj.body.id;
	const asyncTask: AsyncTask | undefined = refineAsyncTask(obj.body);

	if (!asyncTask) {
		return {
			status: obj.status,
			body: {
				id: schemeId,
			},
		};
	}

	return {
		status: obj.status,
		body: { id: schemeId, ...asyncTask },
	};
};

export const refinePriorityOnServerPageResponse = (
	obj: unknown,
): PriorityOnServerPageResponse | undefined => {
	if (!obj || typeof obj !== 'object') return undefined;

	if (!('maxResults' in obj) || typeof obj.maxResults !== 'number') return undefined;

	if (!('startAt' in obj) || typeof obj.startAt !== 'number') return undefined;

	if (!('total' in obj) || typeof obj.total !== 'number') return undefined;

	if (!('isLast' in obj) || typeof obj.isLast !== 'boolean') return undefined;

	if (!('values' in obj) || !Array.isArray(obj.values)) return undefined;

	const refinedValues: PriorityOnServer[] = obj.values.map((e: unknown) => {
		const newMappingPriority: PriorityOnServer | undefined = refinePriorityOnServer(e);
		if (!newMappingPriority) {
			throw new Error('Unexpected shape of Mapping Priority');
		}
		return newMappingPriority;
	});

	return {
		maxResults: obj.maxResults,
		startAt: obj.startAt,
		total: obj.total,
		isLast: obj.isLast,
		values: refinedValues,
	};
};
